/*
 * <moksae-carousel> — self-contained auto-rotating product carousel.
 * Images + videos, dot indicators, prev/next arrows, swipe (native scroll-snap),
 * hover/focus pause, prefers-reduced-motion aware, and pauses autoplay while a
 * video is playing. No dependencies. Scoped to landing-style product pages only.
 */
class MoksaeCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.mk-carousel__track');
    this.slides = Array.from(this.querySelectorAll('.mk-carousel__slide'));
    this.dots = Array.from(this.querySelectorAll('.mk-carousel__dot'));
    this.prevButton = this.querySelector('.mk-carousel__arrow--prev');
    this.nextButton = this.querySelector('.mk-carousel__arrow--next');
    this.videos = Array.from(this.querySelectorAll('video'));

    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
    this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
    this.videoPlaying = false;
    this.timer = null;
  }

  connectedCallback() {
    if (!this.track || this.slides.length === 0) return;

    this.prevButton && this.prevButton.addEventListener('click', () => this.step(-1, true));
    this.nextButton && this.nextButton.addEventListener('click', () => this.step(1, true));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10), true));
    });

    // Keep the active dot in sync with manual swiping / scrolling.
    let scrollRaf = null;
    this.track.addEventListener(
      'scroll',
      () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => this.syncIndexFromScroll());
      },
      { passive: true }
    );

    // Pause autoplay while a video is in use; resume when it stops.
    this.videos.forEach((video) => {
      video.addEventListener('play', () => {
        this.videoPlaying = true;
        this.stopAutoplay();
      });
      ['pause', 'ended'].forEach((evt) =>
        video.addEventListener(evt, () => {
          this.videoPlaying = false;
          this.startAutoplay();
        })
      );
    });

    // Pause on hover / focus, resume on leave.
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    // Pause autoplay when the carousel scrolls out of view.
    if ('IntersectionObserver' in window) {
      this.visible = true;
      const io = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          this.visible ? this.startAutoplay() : this.stopAutoplay();
        },
        { threshold: 0.4 }
      );
      io.observe(this);
    } else {
      this.visible = true;
    }

    this.reducedMotion.addEventListener('change', () => {
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    });

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  slideWidth() {
    return this.slides[0] ? this.slides[0].getBoundingClientRect().width : this.track.clientWidth;
  }

  syncIndexFromScroll() {
    const idx = Math.round(this.track.scrollLeft / this.slideWidth());
    if (idx !== this.index) {
      this.index = Math.max(0, Math.min(idx, this.slides.length - 1));
      this.updateDots();
    }
  }

  goTo(index, userInitiated) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;
    this.track.scrollTo({ left: this.slideWidth() * this.index, behavior: 'smooth' });
    this.updateDots();
    if (userInitiated) this.restartAutoplay();
  }

  step(delta, userInitiated) {
    this.goTo(this.index + delta, userInitiated);
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.videoPlaying || this.visible === false) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.step(1, false), this.speed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('moksae-carousel')) {
  customElements.define('moksae-carousel', MoksaeCarousel);
}
